import type { ActionResult, FeedChannel, UserProfile } from './models'

export const normalizeEmail = (email: string): string => email.trim().toLowerCase()

export const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(email))

export const cleanText = (value: string): string => value.trim().replace(/\s+/g, ' ')

export const normalizeUsername = (value: string): string => value.trim().replace(/^@+/, '')

export const isValidUsername = (value: string): boolean => {
  const normalized = normalizeUsername(value)
  return normalized.length >= 3
    && normalized.length <= 24
    && /^[A-Za-z0-9._]+$/.test(normalized)
}

export const formatUsername = (value: string): string => `@${normalizeUsername(value)}`

const legacyUsername = (profile: Pick<UserProfile, 'id' | 'displayName' | 'username'>): string => {
  const supplied = normalizeUsername(profile.username ?? '')
  if (isValidUsername(supplied)) return supplied
  if (cleanText(profile.displayName).toLowerCase() === 'alex rivera') return 'Alextrextra'
  const stableSuffix = profile.id.replace(/[^A-Za-z0-9]/g, '').slice(-8) || 'onroute'
  return `fan${stableSuffix}`.slice(0, 24)
}

export const profileUsername = (profile: Pick<UserProfile, 'id' | 'displayName' | 'username'>): string =>
  legacyUsername(profile)

export const normalizeUserProfile = (profile: UserProfile): UserProfile => {
  const username = profileUsername(profile)
  const realName = cleanText(profile.realName ?? profile.displayName) || 'Fan'
  return {
    ...profile,
    displayName: cleanText(profile.displayName) || realName,
    realName,
    username,
  }
}

export const isFeedChannel = (value: string): value is FeedChannel =>
  ['main', 'updates', 'fits', 'merch', 'scene'].includes(value)

export const normalizeFeedChannel = (value: string): FeedChannel => {
  if (value === 'energy') return 'scene'
  return isFeedChannel(value) ? value : 'main'
}

export const success = (code: string, message: string): ActionResult => ({ ok: true, code, message })

export const failure = (code: string, message: string): ActionResult => ({ ok: false, code, message })

export const toggleId = (ids: readonly string[], id: string): string[] =>
  ids.includes(id) ? ids.filter((candidate) => candidate !== id) : [...ids, id]

export const uniqueIds = (ids: readonly string[]): string[] => [...new Set(ids.filter(Boolean))]

export const createMonotonicIdFactory = (): ((prefix: string) => string) => {
  let sequence = 0
  return (prefix) => {
    sequence += 1
    const randomId = globalThis.crypto?.randomUUID?.()
    return randomId ? `${prefix}-${randomId}` : `${prefix}-${Date.now().toString(36)}-${sequence.toString(36)}`
  }
}
